/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState } from 'react';
import PlusBenefits from 'components/Plus/PlusBenefits/PlusBenefits';
import { Trans, useTranslation } from 'react-i18next';
import OpButton, { ButtonElement } from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import { useDispatch, useSelector } from 'react-redux';
import addOPPlusSubscriptionAndRedirectToCartOperation from 'store/app/operation/addOPPlusSubscriptionAndRedirectToCartOperation';
import OpText, { TextElement } from 'components/Common/op-ui-wrappers/OpText/OpText';
import Help from 'components/Plus/Help/Help';
import classNames from 'classnames';
import isDesktop from 'store/app/selector/isDesktop';
import styles from './PlusNonMember.module.scss';
import PlusNonMemberBanner from './PlusNonMemberBanner';
import RewardsSection from '../RewardsSection/RewardsSection';

const buildText = (
  element: TextElement & {
    highlight?: boolean;
    label?: string;
    translationKey?: string;
    className?: string;
    interpolationValues?: Record<string, any>;
    componentClassName?: string;
  },
  index: number,
) => {
  const highlightClass = element.highlight ? 'highlight' : '';
  return (
    <OpText key={index} variant={element.variant} className={classNames(highlightClass, styles[element.className])}>
      <Trans
        i18nKey={element.translationKey}
        values={element.interpolationValues}
        components={{
          span: (
            <OpText
              className={element.componentClassName && styles[element.componentClassName]}
              variant="body-large-bold"
            />
          ),
        }}
      />
    </OpText>
  );
};

const buildButton = (
  element: ButtonElement & {
    label?: string;
    translationKey?: string;
    onClick?: () => void;
    content?: any[];
    event: string;
  },
  index: number,
) => {
  if (!element) return null;
  const onClickHandler = () => {
    const event = new CustomEvent(element.event);
    window.dispatchEvent(event);
  };

  return (
    <OpButton
      key={index}
      onClick={onClickHandler}
      variant={element.variant}
      color={element.color}
      icon-right={element['icon-right']}
      className={styles[element.className]}
    >
      {element.content && buildContent(element.content)}
    </OpButton>
  );
};

const buildImage = (element: any, index: number) => {
  if (!element) return null;
  return <img key={index} src={element.src} alt={element.alt} className={styles.imageContainer} />;
};

const buildContent = (content: any[]) => {
  if (!content) return null;
  return content.map((element: any, index: number) => {
    switch (element.type) {
      case CONTENT_TYPES.TEXT:
        return buildText(element, index);
      case CONTENT_TYPES.IMAGE:
        return buildImage(element, index);
      case CONTENT_TYPES.BUTTON:
        return buildButton(element, index);
      default:
        return null;
    }
  });
};

const CONTENT_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
};

export function usePromotionalContent(isDesktopDevice: boolean) {
  const { t } = useTranslation();
  const [content, setContent] = useState<any>(null);

  useEffect(() => {
    try {
      const jsonString: any = t(`txt_non_member_promotional_${isDesktopDevice ? 'desktop' : 'mobile'}`, {
        interpolation: {
          skipOnVariables: true,
        },
      });
      const parsed = JSON.parse(jsonString);
      if (parsed.content) {
        setContent(buildContent(parsed.content));
      }
    } catch (e) {
      console.log('Error trying to parse promotional content', e);
    }
  }, [isDesktopDevice]);

  return content;
}

const PlusNonMember = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktopDevice = useSelector(isDesktop);

  const addOPPlus = () => {
    dispatch(addOPPlusSubscriptionAndRedirectToCartOperation());
  };

  const content = usePromotionalContent(isDesktopDevice);

  useEffect(() => {
    window.addEventListener('addOPPlus', addOPPlus);
    return () => {
      window.removeEventListener('addOPPlus', addOPPlus);
    };
  }, []);

  return (
    <div className={styles.container}>
      <PlusNonMemberBanner />
      <div className={styles.benefits}>
        {isDesktopDevice ? (
          <PlusBenefits size="large" layout="column" title={t('txt_plus_membership_benefits')} />
        ) : (
          <PlusBenefits size="small" layout="row" title={t('txt_plus_membership_benefits')} />
        )}
      </div>
      {content && <div className={styles.promotionalContainer}>{content}</div>}
      <RewardsSection />
      <div className={styles.helpContainer}>
        <Help />
      </div>
    </div>
  );
};

export default PlusNonMember;
